import { useState, useCallback, useEffect, useRef } from 'react';

// Polling configuration
interface PollingConfig {
  interval: number;        // Polling interval in milliseconds
  maxDuration: number;     // Maximum polling duration in milliseconds
}

const DEFAULT_CONFIG: PollingConfig = {
  interval: 3000,          // 3 seconds
  maxDuration: 300000,     // 5 minutes (5 * 60 * 1000)
};

// Product status constants
const PRODUCT_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2,
  DISCONTINUED: 3,
  FETCHING: 4,
  FAILED: 5,
};

export interface UseProductRefetchResult {
  isRefetching: boolean;
  error: string | null;
  startRefetch: () => Promise<void>;
  cancelRefetch: () => void;
}

interface UseProductRefetchOptions {
  productId: number;
  onSuccess: () => void;
  onError?: (error: string) => void;
  config?: Partial<PollingConfig>;
}

/**
 * Custom hook for polling product refetch status
 *
 * Initiates a refetch request and polls the backend every 3 seconds
 * until the product status changes from 'fetching' to 'active' or 'failed'.
 *
 * @param options - Configuration options including productId and callbacks
 * @returns Refetch state and control functions
 */
export const useProductRefetch = ({
  productId,
  onSuccess,
  onError,
  config = {},
}: UseProductRefetchOptions): UseProductRefetchResult => {
  const [isRefetching, setIsRefetching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingConfig = { ...DEFAULT_CONFIG, ...config };
  const pollingStartTime = useRef<number | null>(null);
  const isCancelled = useRef<boolean>(false);

  /**
   * Fetch product status from API
   */
  const fetchProductStatus = useCallback(async (): Promise<number | null> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/knowledge/products/${productId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('tasky_auth_token')}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product status');
      }

      const data = await response.json();
      return data.product?.status ?? data.data?.status ?? null;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to fetch product status');
    }
  }, [productId]);

  /**
   * Execute polling iteration
   */
  const pollStatus = useCallback(async () => {
    if (isCancelled.current) {
      return;
    }

    try {
      const status = await fetchProductStatus();

      if (isCancelled.current) {
        return;
      }

      // Check if refetch completed successfully
      if (status === PRODUCT_STATUS.ACTIVE) {
        setIsRefetching(false);
        setError(null);
        onSuccess();
        return;
      }

      // Check if refetch failed
      if (status === PRODUCT_STATUS.FAILED) {
        const errorMsg = '商品情報の取得に失敗しました。URLを確認して再度お試しください。';
        setIsRefetching(false);
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Check for timeout
      const hasTimeLeft = pollingStartTime.current &&
        (Date.now() - pollingStartTime.current) < pollingConfig.maxDuration;

      if (!hasTimeLeft) {
        const errorMsg = '処理がタイムアウトしました。しばらく待ってからページを再読み込みしてください。';
        setIsRefetching(false);
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Continue polling (status is still FETCHING)
      timeoutRef.current = setTimeout(() => {
        pollStatus();
      }, pollingConfig.interval);

    } catch (err: any) {
      if (isCancelled.current) {
        return;
      }
      console.error('Polling error:', err);
      // Don't stop polling on transient errors, just schedule next poll
      timeoutRef.current = setTimeout(() => {
        pollStatus();
      }, pollingConfig.interval);
    }
  }, [fetchProductStatus, pollingConfig, onSuccess, onError]);

  /**
   * Cancel ongoing refetch polling
   */
  const cancelRefetch = useCallback(() => {
    isCancelled.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsRefetching(false);
    pollingStartTime.current = null;
  }, []);

  /**
   * Start refetch process
   */
  const startRefetch = useCallback(async () => {
    // Cancel any existing polling
    cancelRefetch();
    isCancelled.current = false;

    // Initialize polling state
    setIsRefetching(true);
    setError(null);
    pollingStartTime.current = Date.now();

    try {
      // Send refetch request
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/knowledge/products/${productId}/refetch`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('tasky_auth_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '再取得の開始に失敗しました');
      }

      // Start polling after a short delay to allow job to be queued
      timeoutRef.current = setTimeout(() => {
        pollStatus();
      }, pollingConfig.interval);

    } catch (err: any) {
      const errorMsg = err.message || '再取得の開始に失敗しました';
      setIsRefetching(false);
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [productId, cancelRefetch, pollStatus, pollingConfig.interval, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelRefetch();
    };
  }, [cancelRefetch]);

  return {
    isRefetching,
    error,
    startRefetch,
    cancelRefetch,
  };
};

export default useProductRefetch;
